// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel';
import type { LatLngGeometryModelType } from './LatLngGeometry';
import type { ZoneMapGeometryIdModelType } from './ZoneMapGeometryId';
import type { UserIdModelType } from './UserId';
import type { VerificationStatusModelType } from './VerificationStatus';
import type { ZoneMapVehicleIdModelType } from 'common/models/flow/ZoneMapVehicleId';
import type { VehicleModelIdModelType } from './VehicleModelId';

import { ZoneMapVehicleId } from 'common/models/flow/ZoneMapVehicleId';
import { VehicleModelId } from './VehicleModelId';
import { LatLngGeometry } from './LatLngGeometry';
import { ZoneMapGeometryId } from './ZoneMapGeometryId';
import { UserId } from './UserId';
import { VerificationStatus } from './VerificationStatus';

export type ZoneMapGeometryModelType = {
    id: ZoneMapGeometryIdModelType,
    name: string,
    geometry: LatLngGeometryModelType,
    routeId: string,
    localities: Array<string>,
    apartments: Array<string>,
    pincodes: Array<string>,
    users: Array<UserIdModelType>,
    vehicles: Array<ZoneMapVehicleIdModelType>,
    vehicleModels: Array<VehicleModelIdModelType>,
    description: string,
    visualProperties: Object,
    directoryId: string,
    verificationStatus: VerificationStatusModelType
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class ZoneMapGeometry extends ImmutableModel {
    static fromJS(json: ZoneMapGeometryModelType): ZoneMapGeometry {
        const state: Object = Object.assign({}, json);
        state.id = ZoneMapGeometryId.fromJS(state.id);
        state.geometry = LatLngGeometry.fromJS(state.geometry);
        state.localities = Immutable.List(state.localities);
        state.apartments = Immutable.List(state.apartments);
        state.pincodes = Immutable.List(state.pincodes);
        state.users = Immutable.List(state.users).map(item =>
            UserId.fromJS(item)
        );
        state.vehicles = Immutable.List(state.vehicles).map(item =>
            ZoneMapVehicleId.fromJS(item)
        );
        state.vehicleModels = Immutable.List(state.vehicleModels).map(item =>
            VehicleModelId.fromJS(item)
        );
        state.verificationStatus = VerificationStatus.fromJS(
            state.verificationStatus
        );
        return new this(Immutable.Map(state));
    }

    toJS(): ZoneMapGeometryModelType {
        return {
            id: this.id.toJS(),
            name: this.name,
            geometry: this.geometry.toJS(),
            routeId: this.routeId,
            localities: this.localities.toArray(),
            apartments: this.apartments.toArray(),
            pincodes: this.pincodes.toArray(),
            users: this.users.toArray().map(item => item.toJS()),
            vehicles: this.vehicles.toArray().map(item => item.toJS()),
            vehicleModels: this.vehicleModels
                .toArray()
                .map(item => item.toJS()),
            description: this.description,
            visualProperties: this.visualProperties,
            directoryId: this.directoryId,
            verificationStatus: this.verificationStatus.toJS()
        };
    }

    get id(): ZoneMapGeometryId {
        return this._state.get('id');
    }

    setId(id: ZoneMapGeometryId): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get geometry(): LatLngGeometry {
        return this._state.get('geometry');
    }

    setGeometry(geometry: LatLngGeometry): this {
        return this.clone(this._state.set('geometry', geometry));
    }

    get routeId(): string {
        return this._state.get('routeId');
    }

    setRouteId(routeId: string): this {
        return this.clone(this._state.set('routeId', routeId));
    }

    get localities(): Immutable.List<string> {
        return this._state.get('localities');
    }

    setLocalities(localities: Immutable.List<string>): this {
        return this.clone(this._state.set('localities', localities));
    }

    get apartments(): Immutable.List<string> {
        return this._state.get('apartments');
    }

    setApartments(apartments: Immutable.List<string>): this {
        return this.clone(this._state.set('apartments', apartments));
    }

    get pincodes(): Immutable.List<string> {
        return this._state.get('pincodes');
    }

    setPincodes(pincodes: Immutable.List<string>): this {
        return this.clone(this._state.set('pincodes', pincodes));
    }

    get users(): Immutable.List<UserId> {
        return this._state.get('users');
    }

    setUsers(users: Immutable.List<UserId>): this {
        return this.clone(this._state.set('users', users));
    }

    get vehicles(): Immutable.List<ZoneMapVehicleId> {
        return this._state.get('vehicles');
    }

    setVehicles(vehicles: Immutable.List<ZoneMapVehicleId>): this {
        return this.clone(this._state.set('vehicles', vehicles));
    }

    get vehicleModels(): Immutable.List<VehicleModelId> {
        return this._state.get('vehicleModels');
    }

    setVehicleModels(vehicleModels: Immutable.List<VehicleModelId>): this {
        return this.clone(this._state.set('vehicleModels', vehicleModels));
    }

    get description(): string {
        return this._state.get('description');
    }

    setDescription(description: string): this {
        return this.clone(this._state.set('description', description));
    }

    get visualProperties(): Object {
        return this._state.get('visualProperties');
    }

    setVisualProperties(visualProperties: Object): this {
        return this.clone(
            this._state.set('visualProperties', visualProperties)
        );
    }

    get directoryId(): string {
        return this._state.get('directoryId');
    }

    setDirectoryId(directoryId: string): this {
        return this.clone(this._state.set('directoryId', directoryId));
    }

    get verificationStatus(): VerificationStatus {
        return this._state.get('verificationStatus');
    }

    setVerificationStatus(verificationStatus: VerificationStatus): this {
        return this.clone(
            this._state.set('verificationStatus', verificationStatus)
        );
    }
}
